import { Link } from "@tanstack/react-router";
import { Menu } from "lucide-react";
import { useCallback, useState } from "react";
import { ThemeToggle } from "@/components/layout/ThemeToggle";
import { AppSidebar } from "./AppSidebar";
import { useClaudeConnection } from "./ClaudeConnectionContext";

/** One crumb of the trail. The last is rendered as the current page, not a link. */
export interface Crumb {
  label: string;
  to?: string;
}

/**
 * The audit app's shell: navy sidebar, breadcrumb top bar, content well.
 *
 * Replaces the template's `DashboardLayout` as the app's only shell — settings
 * and admin pages are hosted inside it too, so there is one navigation model
 * rather than two.
 *
 * The sidebar's collapsed state lives here rather than in a context because
 * exactly one component needs it and nothing outside the shell can toggle it.
 */
export function AppShell({ crumbs, children }: { crumbs: Crumb[]; children: React.ReactNode }) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const closeMobile = useCallback(() => setMobileOpen(false), []);

  const trail = crumbs.length > 0 ? crumbs : [{ label: "Solutions", to: "/solutions" }];
  const last = trail.at(-1);
  const leading = trail.slice(0, -1);

  return (
    <div className="pa-shell" data-collapsed={collapsed}>
      {/* Tapping the scrim closes the drawer. It carries no keyboard handler
          because Escape and the nav's own links already cover that, and a
          focusable backdrop would be a tab stop that does nothing visible. */}
      <div
        className="pa-side-scrim"
        data-open={mobileOpen}
        onClick={closeMobile}
        aria-hidden="true"
      />
      <AppSidebar
        collapsed={collapsed}
        onToggleCollapsed={() => setCollapsed((c) => !c)}
        mobileOpen={mobileOpen}
        onNavigate={closeMobile}
      />

      <div className="pa-main">
        <header className="pa-top">
          <div className="flex min-w-0 items-center gap-3">
            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              className="grid size-9 place-items-center rounded-lg border border-[var(--border-1)] bg-white text-[var(--fg-1)] min-[901px]:hidden"
              aria-label="Open navigation"
            >
              <Menu aria-hidden="true" size={18} />
            </button>
            <nav className="pa-crumbs" aria-label="Breadcrumb">
              {leading.map((crumb) => (
                <span key={crumb.label} className="flex items-center gap-2">
                  {crumb.to === undefined ? (
                    <span>{crumb.label}</span>
                  ) : (
                    <Link to={crumb.to}>{crumb.label}</Link>
                  )}
                  <span aria-hidden="true">›</span>
                </span>
              ))}
              <b aria-current="page">{last?.label}</b>
            </nav>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            <ClaudeChip />
            <ThemeToggle />
          </div>
        </header>

        <main className="pa-content">{children}</main>
      </div>
    </div>
  );
}

/**
 * The Claude connection indicator.
 *
 * Always visible, in both states, because "not connected" is the state a user
 * needs to know about BEFORE they try to start a run — a chip that only appeared
 * once connected would leave the important case silent. Links to Account, which
 * is where the connection is managed.
 */
function ClaudeChip() {
  const { connected, account } = useClaudeConnection();
  return (
    <Link
      to="/account"
      className="flex items-center gap-2 rounded-full border py-1.5 pr-3 pl-2 text-xs font-semibold no-underline"
      style={{
        borderColor: connected ? "var(--success)" : "var(--border-1)",
        background: connected ? "var(--success-bg)" : "var(--bg-3)",
        color: connected ? "var(--success-fg)" : "var(--fg-2)",
      }}
      title={
        connected
          ? `Runs execute on ${account}, for this session only`
          : "Starting an audit will ask you to connect Claude"
      }
    >
      <span
        className="size-[7px] rounded-full"
        style={{ background: connected ? "var(--success)" : "var(--fg-3)" }}
        aria-hidden="true"
      />
      {connected ? "Claude connected · session" : "Claude not connected"}
    </Link>
  );
}
